import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable()
export class HttpRequestDataInterceptor implements HttpInterceptor {
  private _delimiterPrefix = '__ERMDelimiter__';
  private _delimiterSuffix = '__ERMDelimiter__';

  // The firewall's XSS rules reject any body containing a raw HTML tag,
  // which kills every rich-text payload (e.g. multiline Collab Hub
  // messages). Angle brackets travel as tokens the backend's
  // RequestDelimiterMiddleware restores, exactly like the SQL keywords
  // below.
  private _ltToken = '__ERMLt__';
  private _gtToken = '__ERMGt__';

  private _aposToken = '__ERMApos__';

  private _eqToken = '__ERMEq__';

  // A pasted URL's own '&' between query params also trips the WAF.
  private _ampToken = '__ERMAmp__';
  private readonly _regexAndOr = new RegExp(
    [
      '\\b(?:' +
        [
          // DDL (Data Definition Language)
          'create',
          'alter',
          'drop',
          'truncate',
          'rename',
          'database',
          'schema',
          'table',
          'view',
          'index',
          'sequence',
          'trigger',
          'procedure',
          'function',

          // DML (Data Manipulation Language)
          'select',
          'insert',
          'update',
          'delete',
          'merge',
          'upsert',

          // DQL (Data Query Language)
          'from',
          'where',
          'group by',
          'having',
          'order by',
          'limit',
          'offset',
          'fetch',
          'top',
          'distinct',
          'all',
          'any',
          'some',
          'exists',

          // Joins
          'join',
          'inner join',
          'left join',
          'right join',
          'full join',
          'cross join',
          'outer join',
          'natural join',

          // Set Operations
          'union',
          'union all',
          'intersect',
          'except',
          'minus',

          // Constraints
          'primary key',
          'foreign key',
          'unique',
          'not null',
          'check',
          'default',
          'constraint',
          'references',
          'on delete',
          'on update',
          'cascade',

          // Logical Operators
          'and',
          'or',
          'not',
          'in',
          'between',
          'like',
          'is',
          'is null',
          'is not null',

          // Functions
          'cast',
          'convert',
          'concat',
          'substring',
          'length',
          'upper',
          'lower',
          'count',
          'sum',
          'avg',
          'min',
          'max',
          'round',
          'floor',
          'ceil',
          'date',
          'datetime',
          'timestamp',
          'now',
          'current_date',
          'current_timestamp',

          // Control Flow
          'case',
          'when',
          'then',
          'else',
          'end',
          'if',
          'ifnull',
          'nullif',
          'coalesce',

          // Transaction Control
          'begin',
          'commit',
          'rollback',
          'savepoint',
          'transaction',

          // Execution
          'exec',
          'execute',
          'call',
          'declare',
          'set',

          // Advanced Features
          'with',
          'as',
          'recursive',
          'partition by',
          'over',
          'row_number',
          'rank',
          'dense_rank',
          'lag',
          'lead',
          'window',

          // Data Types
          'int',
          'integer',
          'bigint',
          'smallint',
          'decimal',
          'numeric',
          'float',
          'real',
          'char',
          'varchar',
          'text',
          'blob',
          'clob',
          'binary',
          'varbinary',
          'date',
          'time',
          'datetime',
          'timestamp',
          'interval',
          'boolean',
          'json',

          // Access Control
          'grant',
          'revoke',
          'deny',
          'role',
          'user',
          'privileges',

          // Database-Specific (add more as needed)
          'show',
          'describe',
          'explain',
          'analyze',
          'vacuum',
          'reindex',
          'use',
          'backup',
          'restore',
          'import',
          'export',
          'zip',
          'entity',
          'entities',
        ]
          .map((keyword) => keyword.replace(/\s+/g, '\\s+'))
          .join('|') +
        ')\\b',

      // Additional complex patterns for SQLi detection (unchanged)
      '(?:\\bhaving\\b\\s?(?:[\'"][^=]{1,10}[\'"]\\s?[=<>]+|\\d{1,10}\\s?[=<>]+))',
      "\\bhaving\\b\\s+(?:'[^=]{1,10}'|\\d{1,10})\\s*?[=<>]",
      'exists\\s(?:s(?:elect\\S(?:if(?:null)?\\s\\(|concat|top)|ystem\\s\\()|' +
        "\\bhaving\\b\\s+\\d{1,10}|'[^=]{1,10}'|\\sselect)",
      '\\bexecute\\s{1,5}[\\w\\.$]{1,5}\\s{0,3}',
    ].join('|'),
    'gi',
  );

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    /**
     * 'jemma is part of "clinsphere" and doesn't have the same deprecated firewall'
     * 'Hence we do not have to apply delimiter transformation for jemma API calls'
     **/
    if (req.url.toLowerCase().includes('jemma')) {
      return next.handle(req);
    }

    let modifiedReq = req;
    if (req.params && req.params.keys().length > 0) {
      const newParams = this._processQueryParams(req.params);
      modifiedReq = modifiedReq.clone({ params: newParams });
    }

    if (['POST', 'PUT', 'PATCH'].includes(req.method) && req.body) {
      const newBody =
        req.body instanceof FormData
          ? this._processFormData(req.body)
          : this._processBody(req.body);
      modifiedReq = modifiedReq.clone({ body: newBody });
    }

    return next.handle(modifiedReq);
  }

  private _transformWord(match: string): string {
    const transformed = match.split('').join('_');
    return `${this._delimiterPrefix}${transformed}${this._delimiterSuffix}`;
  }

  private _transformString(value: string): string {
    return value
      .replace(this._regexAndOr, this._transformWord.bind(this))
      .replace(/&/g, this._ampToken)
      .replace(/</g, this._ltToken)
      .replace(/>/g, this._gtToken)
      .replace(/'/g, this._aposToken);
  }

  private _processQueryParams(params: HttpParams): HttpParams {
    let modifiedParams = params;
    params.keys().forEach((key) => {
      const value = params.get(key);
      if (value) {
        const newValue = this._transformString(value);
        if (newValue !== value) {
          modifiedParams = modifiedParams.set(key, newValue);
        }
      }
    });
    return modifiedParams;
  }

  private _processBody(body: unknown): unknown | Record<string, unknown> {
    if (typeof body === 'string') {
      return this._transformString(body);
    }
    if (Array.isArray(body)) {
      return body.map((item) => this._processBody(item));
    }
    if (body !== null && typeof body === 'object') {
      const newBody: Record<string, unknown> = {};
      Object.keys(body).forEach((key) => {
        newBody[key] = this._processBody(body[key as keyof typeof body]);
      });
      return newBody;
    }
    return body;
  }

  // Transform FormData the same way a JSON body's strings are transformed, re-wrapping the File
  // only when its name actually changes so an untouched upload isn't recopied.
  private _processFormData(formData: FormData): FormData {
    const newFormData = new FormData();
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        const transformedName = this._transformFilename(value.name);
        newFormData.append(
          key,
          transformedName === value.name
            ? value
            : new File([value], transformedName, { type: value.type }),
        );
      } else if (typeof value === 'string') {
        newFormData.append(key, this._transformString(value));
      } else {
        newFormData.append(key, value);
      }
    }
    return newFormData;
  }

  private _transformFilename(name: string): string {
    return this._transformString(name).replace(/=/g, this._eqToken);
  }
}
